import { FontIcon, IContextualMenuItem, ITheme, mergeStyleSets, useTheme } from '@fluentui/react';
import { IPActionButton, IPStack, IPTooltipHost } from 'baseComponents';
import { useTranslation } from 'react-i18next';

export interface IFormStructureAddOption {
  key: string;
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  // A choice behind the option (which audited item to add a criterion for). Menu variant only: the
  // empty-state cards have nowhere to nest a submenu, so a caller that needs one there offers the
  // choices as separate cards instead.
  subMenuItems?: IContextualMenuItem[];
}

interface IFormStructureAddControlProps {
  options: IFormStructureAddOption[];
  // Commands that act on the form as a WHOLE rather than adding something to it (its edit session's
  // Discard). This strip is the form's own toolbar - the scene's command bar above it acts on the list
  // of forms - so those commands belong here, beside Add.
  //
  // A disabled command still shows next to Add: it tells the user the command exists and its title
  // says why it is off. The empty-state cards get only the ones that can actually be used - a brand
  // new form has nothing to discard and should not carry the chrome to say so.
  commands?: IFormStructureAddOption[];
  // 'menu' (default): a single Fluent "+ Add" dropdown button (Microsoft Forms pattern), pinned at the
  // top of a form that already has fields so it stays compact and never gets clipped.
  // 'cards': the empty-state onboarding cards shown in the body when the form has no fields yet.
  variant?: 'menu' | 'cards';
}

const getCardStyles = (theme: ITheme) =>
  mergeStyleSets({
    addCardRow: { display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', paddingTop: 16 },
    addCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      rowGap: 8,
      width: 150,
      padding: '20px 12px',
      cursor: 'pointer',
      borderRadius: 6,
      background: theme.semanticColors.bodyBackground,
      border: `1px solid ${theme.palette.neutralLight}`,
      fontFamily: 'inherit',
      color: theme.palette.neutralPrimary,
      selectors: {
        ':hover': { borderColor: theme.palette.themePrimary, background: theme.palette.neutralLighterAlt },
      },
    },
    addCardIcon: { fontSize: 22, color: theme.palette.themePrimary },
    addCardLabel: { fontSize: 14, fontWeight: 600 },
  });

export const FormStructureAddControl = (props: IFormStructureAddControlProps) => {
  const { t } = useTranslation(['translation']);
  const theme = useTheme();

  // A disabled Fluent button never fires the browser's own title tooltip, and disabled is exactly when
  // the explanation is needed - so a command that has one is wrapped rather than given a title prop.
  const renderCommand = (command: IFormStructureAddOption): JSX.Element => {
    const button = (
      <IPActionButton
        iconProps={{ iconName: command.icon }}
        text={command.label}
        disabled={command.disabled}
        onClick={command.onClick}
      />
    );

    if (!command.title) {
      return button;
    }

    return <IPTooltipHost content={command.title}>{button}</IPTooltipHost>;
  };

  // Empty-state onboarding cards (a form with no fields yet).
  if (props.variant === 'cards') {
    const styles = getCardStyles(theme);
    const usableCommands = (props.commands ?? []).filter((command) => !command.disabled);

    return (
      <IPStack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
        {usableCommands.length > 0 && (
          <IPStack.Item>
            <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              {usableCommands.map((command) => (
                <IPStack.Item key={command.key}>{renderCommand(command)}</IPStack.Item>
              ))}
            </IPStack>
          </IPStack.Item>
        )}
        <IPStack.Item>
          <div className={styles.addCardRow}>
            {props.options.map((o) => (
              <button
                key={o.key}
                type="button"
                className={styles.addCard}
                onClick={o.onClick}
                disabled={o.disabled}
                title={o.title}
                style={o.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <FontIcon iconName={o.icon} className={styles.addCardIcon} />
                <span className={styles.addCardLabel}>{o.label}</span>
              </button>
            ))}
          </div>
        </IPStack.Item>
      </IPStack>
    );
  }

  // Default: a single "+ Add" menu button (Microsoft Forms / Fluent menu-button pattern).
  const menuItems: IContextualMenuItem[] = props.options.map((o) => ({
    key: o.key,
    text: o.label,
    iconProps: { iconName: o.icon },
    onClick: o.subMenuItems ? undefined : () => o.onClick(),
    subMenuProps: o.subMenuItems ? { items: o.subMenuItems } : undefined,
    disabled: o.disabled,
    title: o.title,
  }));

  return (
    <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
      <IPStack.Item>
        <IPActionButton
          iconProps={{ iconName: 'Add' }}
          text={t('translation:General.Button.Add')}
          menuProps={{ items: menuItems }}
        />
      </IPStack.Item>
      {(props.commands ?? []).map((command) => (
        <IPStack.Item key={command.key}>{renderCommand(command)}</IPStack.Item>
      ))}
    </IPStack>
  );
};

export default FormStructureAddControl;
